/**
 * The explicit-ID bulk bar and its reviewed preview (P8-T3).
 *
 * The surface has exactly one selection, and it is the one the person checked row by row — across
 * pages, because the selection is a list of ids that page navigation does not touch. There is no
 * "select all results" here and no place to put one: the bar renders the count it holds and the
 * three central actions (`IMPLEMENTATION-CONTRACTS.md` §5), each of which becomes a request built by
 * `bulk.ts` from those ids and nothing else (`BAL-07`).
 *
 * A preview is shown, not implied: the exact rows the commit will touch are enumerated with their
 * own amounts and dates, the impact is the API's own counts, and the confirm is unavailable — with
 * the reason printed — as soon as the selection or the business day moves away from what was
 * reviewed (`F-30`: "Changing selection or concurrent values requires a new preview").
 */

import { useState } from 'react';
import type { BulkAction, CategoryBody } from '@saldo/contracts';

import { describeSaveState, type SaveState } from './save';
import {
  commitBlocker,
  type BulkOptions,
  type BulkPreviewView,
  type BulkSelection,
} from './bulk';

/** The bulk bar's own state, owned by the workspace so a refresh cannot lose an open preview. */
export interface BulkState {
  /** The action the open preview is for, or the action being prepared. */
  readonly action: BulkAction | null;
  readonly preview: BulkPreviewView | null;
  readonly busy: boolean;
  /** A preview-time failure, already worded. */
  readonly error: string | null;
}

export interface BulkActionsProps {
  readonly selection: BulkSelection;
  readonly categories: readonly CategoryBody[];
  /** The server's business day, from the envelope's `meta`; a preview of another day is stale. */
  readonly businessToday: string;
  readonly state: BulkState;
  readonly saveState: SaveState;
  readonly saveMessage: string | null;
  readonly onClear: () => void;
  readonly onPreview: (action: BulkAction, options: BulkOptions) => void;
  readonly onCommit: () => void;
  readonly onDismiss: () => void;
}

export function BulkActions(props: BulkActionsProps) {
  const [preparing, setPreparing] = useState<BulkAction | null>(null);
  const [categoryId, setCategoryId] = useState<string>('');
  const [paidDate, setPaidDate] = useState<string>('');

  const { selection, state } = props;
  if (selection.length === 0 && state.preview === null) return null;

  const preview = state.preview;
  const blocker = preview === null ? null : commitBlocker(preview, selection, props.businessToday);

  return (
    <section className="preview" aria-label="Selected rows" data-testid="bulk-bar">
      <h3 data-testid="bulk-count">{selection.length} selected</h3>
      <p className="hint">
        Bulk acts on the rows you checked, on this page and the others — never on the filter.
      </p>

      <div className="actions">
        <button
          type="button"
          data-testid="bulk-pay-open"
          disabled={selection.length === 0 || state.busy}
          onClick={() => setPreparing(preparing === 'bulk.pay' ? null : 'bulk.pay')}
        >
          Mark paid
        </button>
        <button
          type="button"
          data-testid="bulk-categorize-open"
          disabled={selection.length === 0 || state.busy}
          onClick={() => setPreparing(preparing === 'bulk.categorize' ? null : 'bulk.categorize')}
        >
          Set category
        </button>
        <button
          type="button"
          data-testid="bulk-cancel-open"
          disabled={selection.length === 0 || state.busy}
          onClick={() => setPreparing(preparing === 'bulk.cancel' ? null : 'bulk.cancel')}
        >
          Cancel entries
        </button>
        <button
          type="button"
          data-testid="bulk-clear"
          disabled={state.busy}
          onClick={() => {
            setPreparing(null);
            props.onClear();
          }}
        >
          Clear selection
        </button>
      </div>

      {preparing === 'bulk.pay' ? (
        <div className="field" data-testid="bulk-pay-form">
          <label className="field">
            <span>Date the money moved</span>
            <input
              type="date"
              data-testid="bulk-paid-date"
              value={paidDate}
              onChange={(event) => setPaidDate(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="primary"
            data-testid="bulk-pay-review"
            disabled={state.busy}
            onClick={() => props.onPreview('bulk.pay', { paidDate })}
          >
            Review payment
          </button>
        </div>
      ) : null}

      {preparing === 'bulk.categorize' ? (
        <div className="field" data-testid="bulk-categorize-form">
          <label className="field">
            <span>Category (one for every selected row)</span>
            <select
              data-testid="bulk-category"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">No category (clear)</option>
              {props.categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="primary"
            data-testid="bulk-categorize-review"
            disabled={state.busy}
            onClick={() => props.onPreview('bulk.categorize', { categoryId: categoryId === '' ? null : categoryId })}
          >
            Review category
          </button>
        </div>
      ) : null}

      {preparing === 'bulk.cancel' ? (
        <div className="actions" data-testid="bulk-cancel-form">
          <button
            type="button"
            className="primary"
            data-testid="bulk-cancel-review"
            disabled={state.busy}
            onClick={() => props.onPreview('bulk.cancel', {})}
          >
            Review cancellation
          </button>
        </div>
      ) : null}

      {state.busy ? <p className="hint">Working…</p> : null}
      {state.error !== null ? (
        <p className="notice bad" data-testid="bulk-error">
          {state.error}
        </p>
      ) : null}

      {preview !== null ? (
        <div data-testid="bulk-preview">
          <h3 data-testid="bulk-preview-action">
            {preview.actionLabel} · {preview.rows.length} row{preview.rows.length === 1 ? '' : 's'}
          </h3>
          <p data-testid="bulk-preview-impact">{preview.impactText}</p>
          <ul className="preview-list" data-testid="bulk-preview-rows">
            {preview.rows.map((row) => (
              <li key={row.entryId} data-testid="bulk-preview-row" data-entry-id={row.entryId}>
                <span data-testid="bulk-preview-description">{row.description}</span>
                <span className="money" data-testid="bulk-preview-amount">
                  {row.amountText}
                </span>
                <span className="meta">
                  <span data-testid="bulk-preview-date">{row.dateText}</span>
                  <span data-testid="bulk-preview-state">{row.stateText}</span>
                </span>
              </li>
            ))}
          </ul>
          {preview.accountTexts.length > 0 ? (
            <ul className="preview-list" data-testid="bulk-preview-accounts">
              {preview.accountTexts.map((account) => (
                <li key={account.accountId} data-account-id={account.accountId}>
                  <span>{account.text}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {blocker !== null ? (
            <p className="notice warn" data-testid="bulk-preview-stale">
              {blocker}
            </p>
          ) : null}

          <div className="actions">
            <button
              type="button"
              className="primary"
              data-testid="bulk-confirm"
              disabled={blocker !== null || state.busy}
              onClick={() => props.onCommit()}
            >
              {preview.confirmLabel}
            </button>
            <button type="button" data-testid="bulk-preview-dismiss" disabled={state.busy} onClick={() => props.onDismiss()}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {props.saveState !== 'idle' ? (
        <p className={props.saveState === 'saved' ? 'hint' : 'notice bad'} data-testid="bulk-write-state">
          {props.saveMessage ?? describeSaveState(props.saveState)}
        </p>
      ) : null}
    </section>
  );
}
